/**
 * ChatPageSkeleton — Structural-mirror shimmer skeleton for the chat loading state.
 *
 * 2026 gold standard: matches the exact layout of the final ChatPage so there's
 * zero Cumulative Layout Shift (CLS) when real data arrives.
 *
 * Two modes:
 * 1. "preview" — We have name + avatar from the conversation list cache.
 *    Only the status line and messages shimmer.
 * 2. "full" — No cached data. Everything shimmers (avatar, name, status, messages).
 */

import { memo } from 'react';
import { ChatAvatar } from './ChatAvatar';
import { VidnightBackground } from './VidnightBackground';
import { PageLayout } from '@/components/layout';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

interface ChatPageSkeletonProps {
  /** Carried-over name from conversation list (null = unknown) */
  name?: string | null;
  /** Carried-over avatar from conversation list (null = unknown) */
  avatar?: string | null;
  /** Navigate back handler */
  onBack?: () => void;
}

// Shimmer line — matches a text line at specific dimensions
function ShimmerLine({ width, height = 14 }: { width: number | string; height?: number }) {
  return (
    <div
      className="animate-pulse rounded-md bg-slate-200/70"
      style={{ width, height }}
    />
  );
}

// Shimmer circle — matches avatar
function ShimmerCircle({ size = 40 }: { size?: number }) {
  return (
    <div
      className="animate-pulse rounded-full bg-slate-200/70"
      style={{ width: size, height: size }}
    />
  );
}

// Shimmer message bubble — structural mirror of MessageBubble
function ShimmerBubble({ isMine, width }: { isMine: boolean; width: string }) {
  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} px-3`}>
      <div className="flex items-end gap-2" style={{ maxWidth: '75%' }}>
        {!isMine && <ShimmerCircle size={28} />}
        <div
          className={`animate-pulse rounded-2xl ${isMine ? 'rounded-se-none bg-neutral-300/40' : 'rounded-ss-none bg-white/50'}`}
          style={{ width, height: 44 }}
        />
      </div>
    </div>
  );
}

export const ChatPageSkeleton = memo(function ChatPageSkeleton({
  name,
  avatar,
  onBack,
}: ChatPageSkeletonProps) {
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const hasPreview = !!name;

  // Top bar: real data where available, shimmer for unknown
  const topBarContent = (
    <>
      {/* Back button — mobile only */}
      {isMobile && (
        <button
          type="button"
          onClick={onBack}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/80"
        >
          <svg className="h-4 w-4 text-neutral-700 rtl:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {/* Avatar + Name + Status — mirrors real top bar layout */}
      <div className="flex flex-1 flex-col overflow-hidden text-start">
        {/* Row 1: Avatar + Name (centered) */}
        <div className="flex items-center gap-2.5">
          <div className="relative flex-shrink-0">
            {hasPreview ? (
              <ChatAvatar
                src={avatar}
                name={name}
                borderColor="border-slate-300"
                fallbackClass="bg-slate-100 text-slate-700"
              />
            ) : (
              <ShimmerCircle size={40} />
            )}
          </div>
          <div className="flex-1 overflow-hidden">
            {hasPreview ? (
              <div className="truncate text-sm font-semibold text-neutral-800">{name}</div>
            ) : (
              <ShimmerLine width={120} height={14} />
            )}
          </div>
        </div>
        {/* Row 2: Status shimmer — below avatar, aligned to its left edge */}
        <div className="flex h-4 items-center">
          <ShimmerLine width={80} height={11} />
        </div>
      </div>

      {/* Search button placeholder */}
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/80 opacity-40">
        <svg className="h-4 w-4 text-neutral-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>
    </>
  );

  return (
    <PageLayout
      topBar={topBarContent}
      contentClassName="relative flex flex-1 flex-col overflow-hidden"
    >
      <VidnightBackground />

      {/* Message area skeleton */}
      <div
        className="flex flex-1 flex-col justify-end gap-3 overflow-hidden py-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
        aria-busy="true"
        aria-live="polite"
      >
        {/* Shimmer bubbles — alternating pattern mimicking a real conversation */}
        <ShimmerBubble isMine={false} width="180px" />
        <ShimmerBubble isMine={false} width="140px" />
        <ShimmerBubble isMine width="200px" />
        <ShimmerBubble isMine width="120px" />
        <ShimmerBubble isMine={false} width="220px" />
        <ShimmerBubble isMine width="160px" />
      </div>

      {/* Input area skeleton */}
      <div className="flex-shrink-0 border-t border-white/20 px-3 pt-2 pb-2">
        <div className="flex items-end gap-2">
          <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200/50" />
          <div className="h-[52px] flex-1 animate-pulse rounded-xl bg-slate-200/50" />
          <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200/50" />
          <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200/50" />
        </div>
      </div>
    </PageLayout>
  );
});
